import {
  breakpoints,
  keyCodes,
  subscribeToResize,
  disableScrollOnElementAttributeChange,
} from '@msc-b2c/shared-utils';
import { bindHeaderClickCta } from './headerAnalytics';

interface TabOptions {
  activeClass?: string;
  defaultTabId?: string | null;
  onTabChange?: (
    tabId: string | null,
    button: HTMLElement | null,
    container: HTMLElement | null
  ) => void;
}

const HEADER_OVERFLOW_TOLERANCE_PX = 1;
const HEADER_UNCOLLAPSE_HYSTERESIS_PX = 16;
const HEADER_COLLAPSED_ATTR = 'isHeaderCollapsed';

export class TabSystem {
  private buttons: HTMLElement[] = [];
  private panels: HTMLElement[] = [];
  private activeTabId: string | null = null;
  private readonly options: TabOptions;
  private readonly body: HTMLElement | null = document.body;
  private overlay: HTMLElement | null = null;
  private wasPreviouslyAboveLgBreakpoint: boolean = window.innerWidth >= breakpoints.lg;
  private bottomContainer: HTMLElement | null = null;
  private activeSubmenuContainer: HTMLElement | null = null;
  private previousContainer: HTMLElement | null = null;
  private expandableContainer: HTMLElement | null = null;
  private mobileContainer: HTMLElement | null = null;
  private mobileFooter: HTMLElement | null = null;
  private upperContainer: HTMLElement | null = null;
  private languageSelectionContainer: HTMLElement | null = null;
  private resizeCleanups: Array<() => void> = [];
  private desktopHeaderRequiredWidth = 0;
  private headerFitObserver: ResizeObserver | null = null;
  private headerFitRaf: number | null = null;
  private navExperienceFragment: HTMLElement | null = null;
  private navExperienceFragmentParent: HTMLElement | null = null;
  private navExperienceFragmentIndex = 0;
  private mobileContainerParentEl: HTMLElement | null = null;
  private shouldSwapHeaderDuplicateIds = false;
  private disableScrollOnExpandableBottomContaineCleanUp: (() => void) | null = null;

  constructor(options: TabOptions = {}) {
    this.options = {
      activeClass: 'active',
      defaultTabId: null,
      onTabChange: undefined,
      ...options,
    };

    this.initialize();
  }

  // Initializes the TabSystem by finding and setting up required elements
  private initialize(): void {
    this.bottomContainer = document.querySelector('.cmp-bottom-container-dark');
    this.expandableContainer = document.querySelector('.cmp-expandable-container');
    this.mobileContainer = document.querySelector('.cmp-mobile');
    this.mobileFooter = document.querySelector('.cmp-mobile-footer');
    this.upperContainer = document.querySelector('.cmp-upper-container');
    this.languageSelectionContainer = document.querySelector('.cmp-top-container');

    if (this.bottomContainer) {
      this.bottomContainer.setAttribute('isExpandableActive', 'false');
      this.bottomContainer.setAttribute('isMobileActive', 'false');
      this.bottomContainer.setAttribute(HEADER_COLLAPSED_ATTR, 'false');
    }

    const buttonElements = document.querySelectorAll('[id^="tab-"][id$="-button"]');
    const panelElements = document.querySelectorAll('[id^="tab-"][id$="-panel"]');
    this.buttons = Array.from(buttonElements) as HTMLElement[];
    this.panels = Array.from(panelElements) as HTMLElement[];

    this.setupOverlay();
    this.setupEventListeners();
    this.setupMenuButtonListeners();
    this.setupTopMenuIconTracking();
    this.setupSubmenuButtonListeners();
    this.setupDesktopHideButtonListeners();
    this.algoliaEditorialSearch();
    this.updateAriaLabelLanguageSelector();
    this.manageHeaderDuplicateIds();
    this.fitHeaderToViewport();
    this.setupHeaderFitObserver();
    this.scheduleFitHeaderToViewport();
    this.syncFavouritesIndicator();
    this.disableScrollOnExpandableBottomContainer();

    window.addEventListener('resize', () => {
      this.disableScrollOnExpandableBottomContainer();
    });

    //wishlist changed (add / remove)
    window.addEventListener('wishlistUpdated', () => {
      this.syncFavouritesIndicator();
    });

    const cleanupNeedHelpResize = subscribeToResize(() => {
      if (!this.upperContainer?.isConnected) {
        cleanupNeedHelpResize();
        this.resizeCleanups = this.resizeCleanups.filter((cb) => cb !== cleanupNeedHelpResize);
        this.headerFitObserver?.disconnect();
        this.headerFitObserver = null;
        if (this.headerFitRaf != null) {
          cancelAnimationFrame(this.headerFitRaf);
          this.headerFitRaf = null;
        }
        return;
      }
      this.fitHeaderToViewport();
    });
    this.resizeCleanups.push(cleanupNeedHelpResize);

    void document.fonts?.ready.then(() => {
      if (this.upperContainer?.isConnected) {
        this.scheduleFitHeaderToViewport();
      }
    });

    if (this.options.defaultTabId) {
      this.activateTab(this.options.defaultTabId);
    }
  }

  private getFavouritesButtons(): HTMLElement[] {
    const buttons: HTMLElement[] = [];

    // Desktop check dot favorites for flag
    const desktopBtn = this.upperContainer?.querySelector('.button.cmp-favourites.-dot-favorites');

    if (desktopBtn instanceof HTMLElement) {
      buttons.push(desktopBtn);
    }

    // Mobile (icon -> closest button with -dot-favorites)
    const mobileIcon = document.querySelector(
      '.cmp-mobile-footer .cmp-button__icon.cmp-button__icon--heart'
    );

    if (mobileIcon instanceof HTMLElement) {
      const mobileBtn = mobileIcon.closest('.button.-dot-favorites');
      if (mobileBtn instanceof HTMLElement) {
        buttons.push(mobileBtn);
      }
    }

    return buttons;
  }

  private syncFavouritesIndicator() {
    const continuity = document
      .querySelector('meta[name="enablePriceTypeContinuity"]')
      ?.getAttribute('content');
    const storageKey = continuity ? 'my-favs' : 'wishlist';

    const storedWishlist = localStorage.getItem(storageKey);

    const wishlist = storedWishlist ? JSON.parse(storedWishlist) : [];

    const hasItems = Array.isArray(wishlist) && wishlist.length > 0;

    this.updateFavouritesIndicator(hasItems);
  }

  private updateFavouritesIndicator(show: boolean) {
    const favouritesButtons = this.getFavouritesButtons();

    favouritesButtons.forEach((btn) => {
      btn.classList.toggle('has-notification', show);
    });
  }

  private isHeaderCollapsed(): boolean {
    return this.bottomContainer?.getAttribute(HEADER_COLLAPSED_ATTR) === 'true';
  }

  // Desktop chrome is used only when the viewport is >= lg AND all header items fit.
  private isDesktopLayout(): boolean {
    return window.innerWidth >= breakpoints.lg && !this.isHeaderCollapsed();
  }

  private getHeaderRow(): HTMLElement | null {
    return this.upperContainer?.querySelector('.cmp-container') ?? null;
  }

  private isSkippedHeaderItem(element: Element): boolean {
    if (!(element instanceof HTMLElement)) return false;
    return (
      element.classList.contains('cmp-menu') ||
      element.classList.contains('-skip-to-main') ||
      !!element.querySelector('.-skip-to-main')
    );
  }

  private setupHeaderFitObserver(): void {
    if (typeof ResizeObserver === 'undefined' || !this.upperContainer) return;

    this.headerFitObserver = new ResizeObserver(() => {
      if (!this.upperContainer?.isConnected) {
        this.headerFitObserver?.disconnect();
        this.headerFitObserver = null;
        return;
      }
      this.scheduleFitHeaderToViewport();
    });
    this.headerFitObserver.observe(this.upperContainer);
  }

  private scheduleFitHeaderToViewport(): void {
    if (this.headerFitRaf != null) return;
    this.headerFitRaf = requestAnimationFrame(() => {
      this.headerFitRaf = null;
      this.fitHeaderToViewport();
    });
  }

  private fitHeaderToViewport(): void {
    this.transformNeedHelpToIcon();
    this.syncCollapsedHeader();
  }

  private syncCollapsedHeader(): void {
    if (!this.upperContainer || !this.bottomContainer) return;
    if (this.bottomContainer.classList.contains('is-editor-mode')) return;

    if (window.innerWidth < breakpoints.lg) {
      this.desktopHeaderRequiredWidth = 0;
      this.applyHeaderCollapsed(false);
      return;
    }

    if (this.isHeaderCollapsed()) {
      const availableWidth = this.getHeaderRow()?.clientWidth ?? 0;
      if (
        this.desktopHeaderRequiredWidth > 0 &&
        availableWidth >= this.desktopHeaderRequiredWidth + HEADER_UNCOLLAPSE_HYSTERESIS_PX
      ) {
        this.applyHeaderCollapsed(false);
        requestAnimationFrame(() => this.fitHeaderToViewport());
      }
      return;
    }

    if (this.doesDesktopHeaderOverflow()) {
      this.desktopHeaderRequiredWidth = this.getDesktopHeaderRequiredWidth();
      this.applyHeaderCollapsed(true);
    }
  }

  private applyHeaderCollapsed(collapsed: boolean): void {
    if (!this.bottomContainer) return;

    const wasCollapsed = this.isHeaderCollapsed();
    if (wasCollapsed === collapsed) return;

    this.bottomContainer.setAttribute(HEADER_COLLAPSED_ATTR, collapsed ? 'true' : 'false');

    if (collapsed) {
      this.handleResizeToLargeBreakpoint();
    }

    this.syncHeaderDuplicateIds();
  }

  private getDesktopHeaderRequiredWidth(): number {
    const row = this.getHeaderRow();
    if (!row) return 0;

    let maxRight = 0;
    const rowLeft = row.getBoundingClientRect().left;

    Array.from(row.children).forEach((child) => {
      if (this.isSkippedHeaderItem(child)) return;
      const el = child as HTMLElement;
      if (window.getComputedStyle(el).display === 'none') return;
      maxRight = Math.max(maxRight, el.getBoundingClientRect().right);
      if (el.classList.contains('experiencefragment')) {
        maxRight = Math.max(maxRight, rowLeft + el.scrollWidth);
      }
    });

    return Math.max(row.scrollWidth, maxRight - rowLeft);
  }

  private doesDesktopHeaderOverflow(): boolean {
    const row = this.getHeaderRow();
    if (!row) return false;

    if (row.scrollWidth > row.clientWidth + HEADER_OVERFLOW_TOLERANCE_PX) {
      return true;
    }

    const rowRect = row.getBoundingClientRect();
    const viewportRight = window.innerWidth;

    return Array.from(row.children).some((child) => {
      if (this.isSkippedHeaderItem(child)) return false;
      const el = child as HTMLElement;
      if (window.getComputedStyle(el).display === 'none') return false;

      const rect = el.getBoundingClientRect();
      if (
        rect.right > viewportRight + HEADER_OVERFLOW_TOLERANCE_PX ||
        rect.right > rowRect.right + HEADER_OVERFLOW_TOLERANCE_PX
      ) {
        return true;
      }

      // Nav labels can clip inside the experience fragment without growing the row
      if (el.classList.contains('experiencefragment')) {
        return el.scrollWidth > el.clientWidth + HEADER_OVERFLOW_TOLERANCE_PX;
      }

      return false;
    });
  }

  // This function dynamically computes if the need help button is wrapping
  // or if its label does not fit, and adds or removes -minimize (icon-only).
  // Uses an off-screen clone to measure wrapping without causing visible layout shifts.
  private transformNeedHelpToIcon() {
    const needHelpButton = this.upperContainer?.querySelector('.button.cmp-help-btn');
    if (!needHelpButton) return;

    // Clone the button off-screen to measure text wrapping without causing CLS
    const clone = needHelpButton.cloneNode(true) as HTMLElement;
    clone.classList.remove('-minimize');
    clone.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;top:-9999px;left:-9999px;width:' +
      (needHelpButton.parentElement?.offsetWidth ?? this.upperContainer?.offsetWidth ?? 0) +
      'px;';
    document.body.appendChild(clone);

    // compute the rects of need help text on the invisible clone
    const buttonText = clone.querySelector('.cmp-button > .cmp-button__text');
    let isInTwoLines = false;
    if (buttonText) {
      const range = document.createRange();
      range.selectNodeContents(buttonText);
      const rects = range.getClientRects();
      isInTwoLines = rects.length > 1;
    }

    clone.remove();

    // Apply or remove -minimize on the real element based on clone measurement
    if (isInTwoLines) {
      needHelpButton.classList.add('-minimize');
    } else {
      needHelpButton.classList.remove('-minimize');
      const btn = needHelpButton as HTMLElement;
      const innerButton = btn.querySelector('.cmp-button') as HTMLElement | null;
      const buttonTextEl = btn.querySelector(
        '.cmp-button > .cmp-button__text'
      ) as HTMLElement | null;
      const overflowsViewport = btn.getBoundingClientRect().right > window.innerWidth + 1;
      // Text is nowrap + overflow hidden, so a too-small button clips instead of wrapping
      const textDoesNotFit =
        (innerButton != null && innerButton.scrollWidth > innerButton.clientWidth + 1) ||
        (buttonTextEl != null && buttonTextEl.scrollWidth > buttonTextEl.clientWidth + 1);
      if (overflowsViewport || textDoesNotFit) {
        needHelpButton.classList.add('-minimize');
      }
    }
  }

  private manageHeaderDuplicateIds() {
    if (!this.upperContainer?.classList.contains('check-duplicate-ids')) return;

    const navExperienceFragment = this.upperContainer.querySelector('.experiencefragment');
    const navExperienceFragmentParent = navExperienceFragment?.parentElement;
    const mobileContainerParent = this.mobileContainer?.parentElement;
    if (!navExperienceFragmentParent || !(navExperienceFragment instanceof HTMLElement)) return;

    this.shouldSwapHeaderDuplicateIds = true;
    this.navExperienceFragment = navExperienceFragment;
    this.navExperienceFragmentParent = navExperienceFragmentParent;
    this.navExperienceFragmentIndex = Array.from(navExperienceFragmentParent.children).indexOf(
      navExperienceFragment
    );
    this.mobileContainerParentEl = mobileContainerParent ?? null;

    this.syncHeaderDuplicateIds();

    let timeout: string | number | NodeJS.Timeout | undefined;
    const cleanupHeaderIdsResize = subscribeToResize(() => {
      if (!this.upperContainer?.isConnected) {
        cleanupHeaderIdsResize();
        this.resizeCleanups = this.resizeCleanups.filter((cb) => cb !== cleanupHeaderIdsResize);
        return;
      }
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.syncHeaderDuplicateIds();
      }, 500);
    });
    this.resizeCleanups.push(cleanupHeaderIdsResize);
  }

  private syncHeaderDuplicateIds(): void {
    if (!this.shouldSwapHeaderDuplicateIds || !this.navExperienceFragmentParent) return;

    const navExperienceFragment = this.navExperienceFragment;
    if (!navExperienceFragment) return;

    if (this.isDesktopLayout()) {
      this.mobileContainer?.remove();
      if (!document.contains(navExperienceFragment)) {
        const refNode =
          this.navExperienceFragmentParent.children[this.navExperienceFragmentIndex] || null;
        this.navExperienceFragmentParent.insertBefore(navExperienceFragment, refNode);
      }
    } else {
      navExperienceFragment.remove();
      if (this.mobileContainer && !document.contains(this.mobileContainer)) {
        this.mobileContainerParentEl?.appendChild(this.mobileContainer);
      }
    }
  }

  //update aria-label of language selector
  private updateAriaLabelLanguageSelector(): void {
    const languageSelectionButton = this.languageSelectionContainer?.querySelector(
      '.cmp-container > .-language-select-aria-label > .cmp-button'
    );
    const ariaLabelText = languageSelectionButton?.querySelector('.cmp-button__text');
    if (languageSelectionButton && ariaLabelText) {
      languageSelectionButton?.setAttribute('aria-label', ariaLabelText?.textContent ?? '');
    }
  }

  // Sets the height of a container relative to the viewport
  private setContainerHeight(container: HTMLElement | null, isExpandable = false): void {
    if (!container || !this.upperContainer) return;

    const upperContainerHeight = this.upperContainer.offsetHeight;

    if (isExpandable && this.isDesktopLayout()) {
      container.style.height = '';
      return;
    }

    const footerHeight = this.mobileFooter?.offsetHeight ?? 0;
    container.style.height = `calc(100svh - ${upperContainerHeight}px - ${footerHeight}px)`;
  }

  // Finds or creates a black overlay element and adds click event listener
  private setupOverlay(): void {
    const existingOverlay = document.querySelector('.black-overlay');

    if (existingOverlay) {
      this.overlay = existingOverlay as HTMLElement;
    } else {
      const overlay = document.createElement('div');
      overlay.classList.add('black-overlay');
      this.body?.appendChild(overlay);
      this.overlay = overlay;
    }

    this.overlay.addEventListener('click', this.handleOverlayClick.bind(this));
  }

  // Handles clicks on the black overlay
  private handleOverlayClick(): void {
    this.resetTabSystem();
    this.closeTab();

    if (this.activeSubmenuContainer) {
      if (this.isDesktopLayout()) {
        this.activeSubmenuContainer.classList.add('hide');
        this.activeSubmenuContainer = null;
      }

      document.querySelectorAll('.button.not-selected').forEach((button) => {
        button.classList.remove('not-selected');
      });
    }

    if (!this.isDesktopLayout() && this.previousContainer) {
      this.previousContainer.classList.remove('hide');
      this.previousContainer = null;
    }

    if (this.overlay) {
      this.overlay.classList.remove('visible');
    }
  }

  // Controls HTML overflow on mobile / collapsed-header layouts only
  private updateHtmlOverflow(shouldHideOverflow: boolean): void {
    if (this.isDesktopLayout()) {
      return;
    }

    if (shouldHideOverflow) {
      document.documentElement.style.overflowY = 'hidden';
      if (document.body) {
        document.body.style.overflowY = 'hidden';
      }
    } else {
      document.documentElement.style.overflowY = '';
      if (document.body) {
        document.body.style.overflowY = '';
      }
    }
  }

  // Sets up desktop hide buttons event listeners
  private setupDesktopHideButtonListeners(): void {
    document.querySelectorAll('.button.-desktop-hide').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.activeSubmenuContainer) {
          this.activeSubmenuContainer.classList.add('hide');
          this.previousContainer?.parentElement?.classList.remove('hide');
          this.activeSubmenuContainer = null;
          this.previousContainer = null;

          document.querySelectorAll('.button.not-selected').forEach((button) => {
            button.classList.remove('not-selected');
          });
        } else {
          if (this.bottomContainer) {
            this.bottomContainer.setAttribute('isExpandableActive', 'false');
            this.bottomContainer.setAttribute('isMobileActive', 'true');
            this.updateHtmlOverflow(true);
          }
          this.resetActiveTab();
        }
      });
    });
  }

  // Resets the active tab state
  private resetActiveTab(): void {
    if (this.activeTabId) {
      const activeButton = document.getElementById(`tab-${this.activeTabId}-button`);
      if (activeButton) {
        activeButton.classList.remove(this.options.activeClass as string);
      }

      const expFragment = this.findExperienceFragment(this.activeTabId);
      if (expFragment) {
        expFragment.classList.add('hide');
      }

      this.activeTabId = null;

      if (this.overlay) {
        this.overlay.classList.remove('visible');
      }

      if (this.options.onTabChange) {
        this.options.onTabChange(null, null, null);
      }
    }
  }

  // Sets up submenu button listeners
  private setupSubmenuButtonListeners(): void {
    document.querySelectorAll('.button.-hasSubmenu').forEach((button) => {
      const siblings = button.parentElement?.querySelectorAll('.button');

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();

        const htmlButton = button as HTMLElement;
        const openContainerTrigger = htmlButton.querySelector('[id^="open-container-"]');
        if (!openContainerTrigger) return;

        const targetMatch = RegExp(/open-container-(.+)/).exec(openContainerTrigger.id);
        if (!targetMatch) return;

        const targetSuffix = targetMatch[1];
        const targetContainerId = `container-${targetSuffix}`;

        const targetContainer = document.getElementById(targetContainerId);
        if (!targetContainer) return;

        const submenuContainer = this.findParentWithClass(targetContainer, '-submenu');
        if (!submenuContainer) return;

        // Apply not-selected class in desktop view when a submenu is opened
        if (this.isDesktopLayout()) {
          document.querySelectorAll('.container.-submenu').forEach((c) => {
            (c as HTMLElement).classList.add('hide');
          });
          document.querySelectorAll('.button.not-selected').forEach((btn) => {
            btn.classList.remove('not-selected');
          });

          if (siblings) {
            siblings.forEach((siblingButton) => {
              if (siblingButton !== button) {
                siblingButton.classList.add('not-selected');
              }
            });
          }
        }

        if (!this.isDesktopLayout()) {
          const parentContainer = this.findParentContainer(htmlButton);
          if (parentContainer && parentContainer !== submenuContainer) {
            const grandparent = parentContainer.parentElement;
            this.previousContainer = parentContainer;
            grandparent?.classList.add('hide');
          }
        }

        submenuContainer.classList.remove('hide');
        this.activeSubmenuContainer = submenuContainer;
      });
    });

    let submenuButtonClicked = false;

    document.querySelectorAll('.button.-hasSubmenu').forEach((button) => {
      button.addEventListener('mousedown', () => {
        submenuButtonClicked = true;
        setTimeout(() => {
          submenuButtonClicked = false;
        }, 10);
      });
    });

    document.addEventListener('click', (event) => {
      if (submenuButtonClicked) return;

      const target = event.target as HTMLElement;

      if (target.closest('.container.-submenu') || target.closest('.button.-hasSubmenu')) {
        return;
      }

      if (this.isDesktopLayout()) {
        document.querySelectorAll('.container.-submenu').forEach((c) => {
          (c as HTMLElement).classList.add('hide');
        });
      }

      document.querySelectorAll('.button.not-selected').forEach((button) => {
        button.classList.remove('not-selected');
      });

      if (!this.isDesktopLayout() && this.previousContainer) {
        this.previousContainer.classList.remove('hide');
        this.previousContainer = null;
      }

      this.activeSubmenuContainer = null;
    });

    document.querySelectorAll('.container.-submenu a').forEach((link) => {
      link.addEventListener('click', () => {
        // Hide submenu container
        if (this.activeSubmenuContainer && this.isDesktopLayout()) {
          this.activeSubmenuContainer.classList.add('hide');
          this.activeSubmenuContainer = null;
        }

        // Remove not-selected classes from buttons
        document.querySelectorAll('.button.not-selected').forEach((button) => {
          button.classList.remove('not-selected');
        });

        // Reset previous container visibility if mobile
        if (!this.isDesktopLayout() && this.previousContainer) {
          this.previousContainer.classList.remove('hide');
          this.previousContainer = null;
        }

        this.resetActiveTab();
        this.handleOverlayClick();
      });
    });
  }

  // Helper to find parent container of an element
  private findParentContainer(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      if (
        parent.classList.contains('container') ||
        parent.classList.contains('experiencefragment') ||
        parent.classList.contains('cmp-container')
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  // Helper to find parent with a specific class
  private findParentWithClass(element: HTMLElement, className: string): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      if (parent.classList.contains(className)) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  // Handles resizing to large breakpoint
  private handleResizeToLargeBreakpoint(): void {
    if (this.bottomContainer) {
      this.bottomContainer.setAttribute('isExpandableActive', 'false');
      this.bottomContainer.setAttribute('isMobileActive', 'false');
    }

    this.updateHtmlOverflow(false);

    if (this.activeSubmenuContainer) {
      this.activeSubmenuContainer.classList.add('hide');
      this.activeSubmenuContainer = null;

      document.querySelectorAll('.button.not-selected').forEach((button) => {
        button.classList.remove('not-selected');
      });
    }

    if (this.previousContainer) {
      this.previousContainer.classList.remove('hide');
      this.previousContainer = null;
    }

    if (this.activeTabId) {
      const activeButton = document.getElementById(`tab-${this.activeTabId}-button`);
      if (activeButton) {
        activeButton.classList.remove(this.options.activeClass as string);
      }
      this.activeTabId = null;
    }

    this.buttons.forEach((button) => {
      button.classList.remove(this.options.activeClass as string);
    });

    if (this.overlay) {
      this.overlay.classList.remove('visible');
    }

    if (this.options.onTabChange) {
      this.options.onTabChange(null, null, null);
    }
  }

  // Sets up tab button and resize event listeners
  private setupEventListeners(): void {
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.handleTabClick.bind(this));
    });

    // when clicking 1 of the 3 btns of the MSC Yacht Club panel
    // the overlay is removed before scrolling to the specified section
    this.panels.forEach((panel) => {
      const btns = panel.querySelectorAll('a.cmp-teaser__action-link');
      btns.forEach((btn) => {
        btn.addEventListener('click', () => this.handleOverlayClick());
      });
    });

    this.wasPreviouslyAboveLgBreakpoint = window.innerWidth >= breakpoints.lg;

    // Subscribe to the singleton resize manager
    const cleanupTabSystemResize = subscribeToResize(({ width }) => {
      if (!this.upperContainer?.isConnected) {
        cleanupTabSystemResize();
        this.resizeCleanups = this.resizeCleanups.filter((cb) => cb !== cleanupTabSystemResize);
        return;
      }
      const isCurrentlyAboveLgBreakpoint = width >= breakpoints.lg;

      if (this.wasPreviouslyAboveLgBreakpoint !== isCurrentlyAboveLgBreakpoint) {
        if (isCurrentlyAboveLgBreakpoint) {
          this.handleResizeToLargeBreakpoint();
        } else if (this.activeTabId) {
          this.resetTabSystem();
          this.resetActiveTab();
        }

        this.wasPreviouslyAboveLgBreakpoint = isCurrentlyAboveLgBreakpoint;
      }

      this.setContainerHeight(this.expandableContainer, true);
      this.setContainerHeight(this.mobileContainer);
    });
    this.resizeCleanups.push(cleanupTabSystemResize);

    // Close active tab when clicking the dark header background (outside buttons/expandable content)
    this.bottomContainer?.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const isInsideButton = !!target.closest('[id$="-button"], .cmp-menu, .cmp-help-btn');
      const isInsideExpandable = !!target.closest('.cmp-expandable-container');
      const isInsideMobile = !!target.closest('.cmp-mobile, .cmp-mobile-footer');

      if (!isInsideButton && !isInsideExpandable && !isInsideMobile && this.activeTabId) {
        this.handleOverlayClick();
      }
    });
  }

  // Resets tab system to default state
  private resetTabSystem(): void {
    if (this.bottomContainer) {
      this.bottomContainer.setAttribute('isExpandableActive', 'false');
      this.bottomContainer.setAttribute('isMobileActive', 'false');
    }

    this.updateHtmlOverflow(false);

    const allTabIds = this.getAllTabIds();
    allTabIds.forEach((tabId) => {
      const expFragment = this.findExperienceFragment(tabId);
      if (expFragment) {
        expFragment.classList.add('hide');
      }
    });

    if (this.overlay) {
      this.overlay.classList.remove('visible');
    }

    this.buttons.forEach((button) => {
      button.classList.remove(this.options.activeClass as string);
    });

    document.querySelectorAll('.button.not-selected').forEach((button) => {
      button.classList.remove('not-selected');
    });
  }

  // Sets up menu open/close button listeners
  private setupMenuButtonListeners(): void {
    const openMenuButton = document.getElementById('open-menu');
    const closeMenuButton = document.getElementById('close-menu');
    const activateOnKeyboard = (event: KeyboardEvent) => {
      if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
        event.preventDefault();
        (event.currentTarget as HTMLElement)?.click();
      }
    };

    openMenuButton?.addEventListener('click', () => {
      if (this.bottomContainer) {
        this.bottomContainer.setAttribute('isExpandableActive', 'false');
        this.bottomContainer.setAttribute('isMobileActive', 'true');
        this.updateHtmlOverflow(true);
      }

      this.setContainerHeight(this.mobileContainer);
      this.setContainerHeight(this.expandableContainer, true);
    });
    openMenuButton?.addEventListener('keydown', activateOnKeyboard);

    closeMenuButton?.addEventListener('click', () => {
      if (this.bottomContainer) {
        this.bottomContainer.setAttribute('isExpandableActive', 'false');
        this.bottomContainer.setAttribute('isMobileActive', 'false');
        this.updateHtmlOverflow(false);
      }

      if (this.activeSubmenuContainer) {
        this.activeSubmenuContainer.classList.add('hide');
        this.activeSubmenuContainer = null;
      }

      if (this.previousContainer) {
        this.previousContainer.parentElement?.classList.remove('hide');
        this.previousContainer = null;
      }

      document.querySelectorAll('.button.not-selected').forEach((button) => {
        button.classList.remove('not-selected');
      });

      if (!this.isDesktopLayout()) {
        if (this.overlay) {
          this.overlay.classList.remove('visible');
        }

        this.resetActiveTab();
      }
    });
    closeMenuButton?.addEventListener('keydown', activateOnKeyboard);
  }

  private setupTopMenuIconTracking(): void {
    const upper = this.upperContainer ?? document.querySelector('.cmp-upper-container');
    if (!upper) return;

    const iconWrappers = upper.querySelectorAll('.cmp-button__secondary.-icon-only');
    iconWrappers.forEach((wrapper) => {
      const clickable = wrapper.querySelector<HTMLElement>('a.cmp-button, button.cmp-button');
      if (clickable) {
        bindHeaderClickCta(clickable);
      }
    });
  }

  // Handles tab button click events
  private handleTabClick(event: Event): void {
    const button = event.currentTarget as HTMLElement;
    const tabId = this.getTabIdFromButton(button);

    if (tabId) {
      const isTogglingActiveTab = this.activeTabId === tabId;

      if (isTogglingActiveTab) {
        this.closeTab();
      } else {
        this.activateTab(tabId);
      }
    }
  }

  // Gets tab ID from button element
  private getTabIdFromButton(button: HTMLElement): string | null {
    const match = RegExp(/tab-(.+)-button/).exec(button.id);
    return match ? match[1] : null;
  }

  // Finds experience fragment element for a specific tab
  private findExperienceFragment(tabId: string): HTMLElement | null {
    const tabPanel = document.getElementById(`tab-${tabId}-panel`);
    if (!tabPanel) return null;

    let parent = tabPanel.parentElement;
    while (parent) {
      if (parent.classList.contains('experiencefragment')) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  // Gets all tab IDs
  private getAllTabIds(): string[] {
    return this.buttons
      .map((button) => this.getTabIdFromButton(button))
      .filter((id): id is string => id !== null);
  }

  // Activates a tab by ID
  private activateTab(tabId: string): void {
    if (this.activeTabId === tabId) return;

    this.resetTabSystem();
    this.activeTabId = tabId;

    const activeButton = document.getElementById(`tab-${tabId}-button`);
    if (activeButton) {
      activeButton.classList.add(this.options.activeClass as string);
    }

    if (this.bottomContainer) {
      this.bottomContainer.setAttribute('isExpandableActive', 'true');
      this.bottomContainer.setAttribute('isMobileActive', 'false');
      this.updateHtmlOverflow(true);
    }

    if (this.overlay) {
      this.overlay.classList.add('visible');
    }

    const expFragment = this.findExperienceFragment(tabId);
    if (expFragment) {
      expFragment.classList.remove('hide');
    }

    if (this.options.onTabChange) {
      const activePanel = document.getElementById(`tab-${tabId}-panel`);
      this.options.onTabChange(tabId, activeButton, activePanel);
    }
  }

  // Closes the active tab
  private closeTab(): void {
    if (this.activeTabId === null) return;

    const activeButton = document.getElementById(`tab-${this.activeTabId}-button`);
    this.resetTabSystem();

    if (activeButton) {
      activeButton.classList.remove(this.options.activeClass as string);
    }

    this.activeTabId = null;

    if (this.options.onTabChange) {
      this.options.onTabChange(null, null, null);
    }
  }
  private algoliaEditorialSearch(): void {
    const searchBlocks = document.querySelectorAll('.cmp-algolia-search');

    searchBlocks.forEach((block) => {
      const input = block.querySelector<HTMLInputElement>('input');
      const button = block.querySelector<HTMLAnchorElement>('.button a');

      if (!input || !button) return;

      const baseUrl = new URL(button.href).origin + new URL(button.href).pathname;

      const triggerSearch = () => {
        const query = input.value.trim();
        if (query) {
          const encodedQuery = encodeURIComponent(query);
          window.location.href = `${baseUrl}?query=${encodedQuery}`;
        }
      };

      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          triggerSearch();
        }
      });

      button.addEventListener('click', (e) => {
        e.preventDefault();
        triggerSearch();
      });
    });
  }

  // Disable scroll on expandable bottom container
  private disableScrollOnExpandableBottomContainer(): void {
    if (window.innerWidth >= 1024) {
      const bottomContainerDarkElement = document.querySelector<HTMLDivElement>(
        '.cmp-bottom-container-dark'
      );
      if (!bottomContainerDarkElement) return;
      this.disableScrollOnExpandableBottomContaineCleanUp = disableScrollOnElementAttributeChange(
        bottomContainerDarkElement,
        'isexpandableactive'
      );
    } else {
      this.disableScrollOnExpandableBottomContaineCleanUp?.();
      this.disableScrollOnExpandableBottomContaineCleanUp = null;
    }
  }
}

// Initialize the tab system
function initTabSystem(): void {
  try {
    new TabSystem({
      activeClass: 'active',
      onTabChange: (tabId, button, container) => {},
    });
  } catch (error) {
    console.error('Error initializing TabSystem:', error);
  }
}

// Initialize when the DOM is ready
if (document.readyState !== 'loading') {
  initTabSystem();
  window.dispatchEvent(new Event('wishlistUpdated'));
} else {
  document.addEventListener('DOMContentLoaded', initTabSystem);
  window.dispatchEvent(new Event('wishlistUpdated'));
}
